import { useTranslation } from 'react-i18next';
import { LegalLayout, Section, Bullets, Bullet } from './LegalLayout';

/**
 * Public Support page.
 *
 * Exists because Microsoft Marketplace certification rejects offers whose
 * "Customer support" link points at a Swagger / docs page (links must lead
 * "directly to support pages... helpful resources at least one of the
 * following must be provided: telephone number(s), email support,
 * contact form(s), chat agents, community/forums").
 *
 * Same `LegalLayout` shell as Privacy / Terms to keep the public site
 * visually consistent and indexable.
 */
export function SupportPage() {
  const { t } = useTranslation();
  return (
    <LegalLayout
      eyebrow={t('support.eyebrow', { defaultValue: 'Support' }) as string}
      title={t('support.title', { defaultValue: 'Customer Support' })}
      updated="2026-06-01"
    >
      <Section
        id="overview"
        kicker="01"
        title={t('support.s1.title', { defaultValue: 'How to reach us' }) as string}
      >
        <p>
          {t('support.s1.body', {
            defaultValue:
              "Knowlez is operated by Info Inlet Inc. (Yokohama, Japan). We respond to support requests in English and Japanese, Monday–Friday during Japan Standard Time (JST, UTC+9) business hours, with best-effort coverage outside those hours. For paying customers we target a first-response time under one business day; severity-1 incidents are paged 24×7.",
          })}
        </p>
      </Section>

      <Section
        id="channels"
        kicker="02"
        title={t('support.s2.title', { defaultValue: 'Channels' }) as string}
      >
        <Bullets>
          <Bullet>
            <strong>Email — general support:</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="mailto:contact@infoinlet.com">
              contact@infoinlet.com
            </a>
            . Best for product questions, billing, integrations, and onboarding help.
          </Bullet>
          <Bullet>
            <strong>Email — engineering / incident:</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="mailto:nymul@infoinlet.com">
              nymul@infoinlet.com
            </a>
            . Use this for outages, API errors with reproduction steps, or anything time-sensitive.
          </Bullet>
          <Bullet>
            <strong>Phone — Yokohama office:</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="tel:+81455505370">
              +81-45-550-5370
            </a>
            . JST business hours (Mon–Fri, 09:00–18:00 JST).
          </Bullet>
          <Bullet>
            <strong>Phone — engineering on-call (Marketplace customers):</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="tel:+819096745832">
              +81-90-9674-5832
            </a>
            . Used for production incidents only; please email first when possible.
          </Bullet>
          <Bullet>
            <strong>Privacy / GDPR / APPI requests:</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="mailto:privacy@knowlez.com">
              privacy@knowlez.com
            </a>
            . Handled per the{' '}
            <a className="text-indigo-600 hover:underline" href="/legal/privacy">
              Privacy Policy
            </a>
            .
          </Bullet>
        </Bullets>
      </Section>

      <Section
        id="business"
        kicker="03"
        title={t('support.s3.title', { defaultValue: 'Business address' }) as string}
      >
        <p>
          Info Inlet Inc. <br />
          Yokohama, Kanagawa, Japan <br />
          Phone: <a className="text-indigo-600 hover:underline" href="tel:+81455505370">+81-45-550-5370</a> <br />
          Email:{' '}
          <a className="text-indigo-600 hover:underline" href="mailto:contact@infoinlet.com">
            contact@infoinlet.com
          </a>
        </p>
      </Section>

      <Section
        id="self-serve"
        kicker="04"
        title={t('support.s4.title', { defaultValue: 'Before you contact us' }) as string}
      >
        <Bullets>
          <Bullet>
            <strong>API status:</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="/status">
              /status
            </a>{' '}
            shows whether the service or any dependency is currently degraded.
          </Bullet>
          <Bullet>
            <strong>Changelog:</strong>{' '}
            <a className="text-indigo-600 hover:underline" href="/changelog">
              /changelog
            </a>{' '}
            lists recent releases and breaking changes.
          </Bullet>
          <Bullet>
            <strong>Security disclosure:</strong> for vulnerabilities, please email{' '}
            <a className="text-indigo-600 hover:underline" href="mailto:security@knowlez.com">
              security@knowlez.com
            </a>{' '}
            with reproduction steps; we coordinate disclosure timelines per the{' '}
            <a className="text-indigo-600 hover:underline" href="/security">
              security policy
            </a>
            .
          </Bullet>
          <Bullet>
            <strong>Open-source / SDK issues:</strong> the funnel-client npm
            packages (<code>@infoinlet/mcp-jp-doc-intel</code>,{' '}
            <code>@infoinlet/mcp-knowledge-rag</code>, …) accept issues on
            GitHub at{' '}
            <a className="text-indigo-600 hover:underline" href="https://github.com/infoinlet-marketplace/marketplace/issues">
              infoinlet-marketplace/marketplace
            </a>
            .
          </Bullet>
        </Bullets>
      </Section>

      <Section
        id="response"
        kicker="05"
        title={t('support.s5.title', { defaultValue: 'What to include in your email' }) as string}
      >
        <p>
          Faster resolution if you include: workspace ID (or subscription ID
          if you reached us via the Microsoft Marketplace), the API key{' '}
          <em>label</em> (never the key itself), the endpoint and timestamp,
          and either the response body or a request ID from the{' '}
          <code>x-request-id</code> response header. We never need your raw API
          key — please don&apos;t paste it.
        </p>
      </Section>
    </LegalLayout>
  );
}
